
/* eslint-disable camelcase -- Firestore field names */
import firebase from 'firebase/compat/app'
import moment from 'moment-timezone'
import Mixins from '@/shared'
import StandingsTable from '@/components/StandingsTable.component.vue'

interface StandingSnapshot {
  id: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  table?: any[]
  // eslint-disable-next-line camelcase, @typescript-eslint/no-explicit-any -- Firestore field name
  created_at?: any
  groupId?: string
}

export default {
  name: 'GroupStatisticsPage',
  components: {
    StandingsTable
  },
  mixins: [Mixins],
  props: {
    competitionId: {
      type: String,
      required: true
    },
    groupId: {
      type: String,
      required: true
    }
  },
  data: () => ({
    loading: true,
    competitionData: {
      ageGroup: '',
      typeId: '',
      id: '',
      name: '' as string
    },
    groupData: {
      name: '' as string,
      cup: false as boolean
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    seasonStart: null as any,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    seasonEnd: null as any,
    seasonResolved: false,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    displaySnapshots: [] as StandingSnapshot[]
  }),
  computed: {
    title (): string {
      const competitionName = this.competitionData.name || ''
      const groupName = this.groupData.name || ''
      if (competitionName && groupName) {
        return `${competitionName} : ${groupName}`
      }
      return groupName || competitionName
    },
    seasonFallback (): boolean {
      return !this.seasonResolved && this.displaySnapshots.length > 0
    }
  },
  async mounted (): Promise<void> {
    await this.loadPage()
  },
  methods: {
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    async loadPage () {
      this.loading = true
      try {
        await Promise.all([
          this.loadCompetitionAndSeason(),
          this.loadGroup(),
          this.loadAndSelectSnapshots()
        ])
      } finally {
        this.loading = false
      }
    },
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    async loadCompetitionAndSeason () {
      await this.$store.dispatch('CompetitonsModule/fetchAndAdd', {
        where: [['id', '==', this.competitionId]]
      })
      const competition =
        this.$store.state.CompetitonsModule.data[this.competitionId]
      if (!competition) {
        return
      }
      this.competitionData = competition

      if (!competition.typeId) {
        return
      }

      await this.$store.dispatch('CompetitionTypesModule/fetchAndAdd', {
        where: [['id', '==', competition.typeId]]
      })
      const competitionType =
        this.$store.state.CompetitionTypesModule.data[competition.typeId]
      if (!competitionType || !competitionType.seasonId) {
        return
      }

      await this.$store.dispatch('SeasonModule/fetchAndAdd', {
        where: [['id', '==', competitionType.seasonId]]
      })
      const season =
        this.$store.state.SeasonModule.data[competitionType.seasonId]
      if (season) {
        this.seasonStart = season.start
        this.seasonEnd = season.end
        this.seasonResolved = !!(season.start && season.end)
      }
    },
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    async loadGroup () {
      await this.$store.dispatch('GroupModule/fetchAndAdd', {
        where: [['id', '==', this.groupId]]
      })
      const group = this.$store.state.GroupModule.data[this.groupId]
      if (group) {
        this.groupData = group
      }
    },
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    async loadAndSelectSnapshots () {
      const snap = await firebase.firestore()
        .collection('standings')
        .where('groupId', '==', this.groupId)
        .get()

      if (snap.empty) {
        this.displaySnapshots = []
        return
      }

      const rows: StandingSnapshot[] = snap.docs.map(d => ({
        id: d.id,
        ...d.data()
      })) as StandingSnapshot[]

      rows.sort((a, b) => this.snapshotMillis(a) - this.snapshotMillis(b))
      this.displaySnapshots = this.selectMonthlySnapshots(rows)
    },
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    snapshotMillis (snapshot: StandingSnapshot): number {
      const created = snapshot.created_at
      if (created && typeof created.toMillis === 'function') {
        return created.toMillis()
      }
      if (created && typeof created.seconds === 'number') {
        return created.seconds * 1000
      }
      return 0
    },
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    monthKey (date: moment.Moment): string {
      return date.format('YYYY-MM')
    },
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    toMoment (value: unknown): moment.Moment | null {
      if (!value) {
        return null
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const v = value as any
      if (typeof v.toDate === 'function') {
        return moment(v.toDate()).tz(process.env.VUE_APP_TIMEZONE || 'UTC')
      }
      if (typeof v.seconds === 'number') {
        return moment.unix(v.seconds).tz(process.env.VUE_APP_TIMEZONE || 'UTC')
      }
      const m = moment(v)
      return m.isValid() ? m.tz(process.env.VUE_APP_TIMEZONE || 'UTC') : null
    },
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    selectMonthlySnapshots (sortedAsc: StandingSnapshot[]): StandingSnapshot[] {
      if (!sortedAsc.length) {
        return []
      }

      const latest = sortedAsc[sortedAsc.length - 1]
      const latestMonth = this.toMoment(latest.created_at)
      const latestMonthKey = latestMonth ? this.monthKey(latestMonth) : null

      const firstByMonth = new Map<string, StandingSnapshot>()
      for (const row of sortedAsc) {
        const m = this.toMoment(row.created_at)
        if (!m) {
          continue
        }
        const key = this.monthKey(m)
        if (!firstByMonth.has(key)) {
          firstByMonth.set(key, row)
        }
      }

      const seasonStart = this.toMoment(this.seasonStart)
      const seasonEnd = this.toMoment(this.seasonEnd)
      const selected: StandingSnapshot[] = []

      if (seasonStart && seasonEnd && seasonStart.isValid() && seasonEnd.isValid()) {
        const cursor = seasonStart.clone().startOf('month')
        const endMonth = seasonEnd.clone().startOf('month')
        while (cursor.isSameOrBefore(endMonth, 'month')) {
          const key = this.monthKey(cursor)
          const first = firstByMonth.get(key)
          if (first) {
            // Skip first-of-month for the latest month when a later snapshot will be shown as current
            if (key === latestMonthKey && first.id !== latest.id) {
              // do not push first-of-month for current month
            } else if (first.id !== latest.id) {
              selected.push(first)
            } else {
              // first-of-month IS the latest — leave it for the final slot
            }
          }
          cursor.add(1, 'month')
        }
      } else {
        // Fallback: all first-of-month snapshots, excluding latest month duplicate handling
        const keys = Array.from(firstByMonth.keys()).sort()
        for (const key of keys) {
          const first = firstByMonth.get(key)
          if (!first) continue
          if (key === latestMonthKey && first.id !== latest.id) {
            continue
          }
          if (first.id !== latest.id) {
            selected.push(first)
          }
        }
      }

      // Always end with the latest snapshot
      selected.push(latest)

      // Defensive: ensure chronological order and unique ids
      const seen = new Set<string>()
      return selected.filter(s => {
        if (seen.has(s.id)) return false
        seen.add(s.id)
        return true
      }).sort((a, b) => {
        // Keep latest last even if equal millis
        if (a.id === latest.id) return 1
        if (b.id === latest.id) return -1
        return this.snapshotMillis(a) - this.snapshotMillis(b)
      })
    },
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    snapshotHeading (snapshot: StandingSnapshot, index: number): string {
      const isLast = index === this.displaySnapshots.length - 1
      if (isLast) {
        return this.$t('latest-standings') as string
      }
      const m = this.toMoment(snapshot.created_at)
      if (!m) {
        return this.$t('standings-table') as string
      }
      return m.format('MMMM YYYY')
    },
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    formatSnapshotDate (createdAt: unknown): string {
      const m = this.toMoment(createdAt)
      if (!m) {
        return '—'
      }
      return m.format('DD/MM/YYYY H:mm')
    }
  }
}
